<script setup lang="ts">
/**
 * 「排列」页签：层级 / 对齐 / 分布 / 结构与剪贴板。
 * 与参考产品把「对齐与分布」放在图标栏不同，这里收进右栏 —— 避免为一组按钮再发明一套浮层交互。
 */
import { computed } from 'vue';
import { useEditor } from '@/composables/useEditor';
import type { AlignMode, DistributeMode } from '@/core/ops/align';

const editor = useEditor();
const count = computed(() => editor.props.value?.count ?? 0);
/** 位置锁：对齐 / 分布都会挪位置，文档原有图元被锁住时这两组直接禁用（引擎侧还有一道兜底） */
const canMove = computed(() => editor.canMoveSelection.value);

const ALIGN: ReadonlyArray<{ mode: AlignMode; label: string }> = [
  { mode: 'left', label: '左' },
  { mode: 'hcenter', label: '中' },
  { mode: 'right', label: '右' },
  { mode: 'top', label: '上' },
  { mode: 'vcenter', label: '垂' },
  { mode: 'bottom', label: '下' },
];
const DISTRIBUTE: ReadonlyArray<{ mode: DistributeMode; label: string }> = [
  { mode: 'horizontal', label: '水平等距' },
  { mode: 'vertical', label: '垂直等距' },
];
</script>

<template>
  <div class="sec">
    <div class="sec-head">
      <span>层级</span>
      <span class="grow" />
      <span class="panel-note">已选 {{ count }}</span>
    </div>
    <div class="seg">
      <button :disabled="count === 0" @click="editor.reorderSelection('front')">置顶</button>
      <button :disabled="count === 0" @click="editor.reorderSelection('forward')">上移</button>
      <button :disabled="count === 0" @click="editor.reorderSelection('backward')">下移</button>
      <button :disabled="count === 0" @click="editor.reorderSelection('back')">置底</button>
    </div>
  </div>

  <div class="sec">
    <div class="sec-head">
      <span>对齐</span><span class="grow" />
      <span v-if="!canMove" class="panel-note">位置已锁定</span>
      <span v-else class="panel-note">需 ≥2 个</span>
    </div>
    <div class="seg">
      <button
        v-for="a in ALIGN"
        :key="a.mode"
        :disabled="count < 2 || !canMove"
        :title="`对齐：${a.label}`"
        @click="editor.alignSelection(a.mode)"
      >
        {{ a.label }}
      </button>
    </div>

    <div class="sec-head" style="margin-top: 10px"><span>分布</span><span class="grow" /><span class="panel-note">需 ≥3 个</span></div>
    <div class="seg">
      <button
        v-for="d in DISTRIBUTE"
        :key="d.mode"
        :disabled="count < 3 || !canMove"
        @click="editor.distributeSelection(d.mode)"
      >
        {{ d.label }}
      </button>
    </div>
  </div>

  <div class="sec">
    <div class="sec-head"><span>结构与剪贴板</span></div>
    <div class="seg">
      <button :disabled="count < 2" title="编组 Ctrl+G" @click="editor.groupSelection()">编组</button>
      <button :disabled="count === 0" title="解组 Ctrl+Shift+G" @click="editor.ungroupSelection()">解组</button>
    </div>
    <div class="seg" style="margin-top: 4px">
      <button :disabled="count === 0" title="复制 Ctrl+C" @click="editor.copySelection()">复制</button>
      <button :disabled="count === 0" title="剪切 Ctrl+X" @click="editor.cutSelection()">剪切</button>
      <button :disabled="!editor.canPaste.value" title="粘贴 Ctrl+V" @click="editor.pasteClipboard()">粘贴</button>
      <button :disabled="count === 0" title="原地复制 Ctrl+D" @click="editor.duplicateSelection()">原地复制</button>
    </div>
    <div class="seg" style="margin-top: 4px">
      <button :disabled="count === 0" title="删除 Delete" class="is-danger" @click="editor.deleteSelection()">
        删除元素
      </button>
    </div>
  </div>

  <div class="sec">
    <div class="sec-head"><span>视图</span></div>
    <div class="seg">
      <button @click="editor.zoomFit()" title="适应窗口 Ctrl+1">适应窗口</button>
      <button :disabled="count === 0" @click="editor.zoomToSelection()" title="适应选中 Ctrl+2">适应选中</button>
      <button @click="editor.setZoom(1)" title="100% Ctrl+0">100%</button>
    </div>
  </div>
</template>
